import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Trash2, ShieldQuestion, Loader2, PauseCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  "Not enough opportunities",
  "Not enough creators/businesses near me",
  "I'm not using Nearfluence enough",
  "Too expensive",
  "I'm not getting enough value",
  "Technical problems",
  "Social account connection problems",
  "Privacy concerns",
  "I created another account",
  "Taking a break",
  "Other",
];

// Data & account controls: export data, deactivate (recommended) or permanently delete.
export function AccountControls() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deactOpen, setDeactOpen] = useState(false);
  const [step, setStep] = useState(0); // delete flow: 0 closed, 1 intro, 2 reason, 3 confirm
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "nearfluence-my-data.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch { toast.error("Could not export your data"); }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.post("/me/deactivate");
      toast.success("Your account has been deactivated");
      logout();
      navigate("/");
    } catch { toast.error("Could not deactivate your account"); setBusy(false); }
  };

  const resetDelete = () => { setStep(0); setReason(""); setDetails(""); setPassword(""); };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/me", { data: { reason, details: reason === "Other" ? details : "", password } });
      toast.success("Your account has been deleted");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete your account");
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-controls" className="rounded-3xl border border-zinc-200 bg-white p-6">
      <h2 className="font-display text-lg font-bold text-zinc-900">Your data & account</h2>
      <p className="mt-1 text-sm text-zinc-500">Manage your personal information and privacy.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" data-testid="export-data-btn" onClick={exportData} className="rounded-full"><Download className="mr-2 h-4 w-4" /> Download my data</Button>
        <Button variant="outline" asChild className="rounded-full"><Link to="/contact?category=Privacy%2Fdata%20request" data-testid="privacy-request-btn"><ShieldQuestion className="mr-2 h-4 w-4" /> Privacy request</Link></Button>
      </div>

      {/* Recommended (safer) vs permanent */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-4">
          <div className="flex items-center gap-2"><PauseCircle className="h-5 w-5 text-emerald-600" /><span className="font-semibold text-zinc-900">Deactivate account</span><span className="ml-auto rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">Recommended</span></div>
          <p className="mt-1.5 text-sm text-zinc-600">Take a break — temporarily hide your profile. Everything is kept and returns when you reactivate.</p>
          <Button data-testid="deactivate-account-btn" onClick={() => setDeactOpen(true)} className="mt-3 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">Deactivate account</Button>
        </div>
        <div className="rounded-2xl border border-zinc-200 p-4">
          <div className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-500" /><span className="font-semibold text-zinc-900">Delete account permanently</span></div>
          <p className="mt-1.5 text-sm text-zinc-600">Permanently remove your account and profile. This cannot be undone.</p>
          <Button variant="outline" data-testid="delete-account-btn" onClick={() => setStep(1)} className="mt-3 w-full rounded-full border-rose-200 text-rose-600 hover:bg-rose-50">Delete account permanently</Button>
        </div>
      </div>

      {/* Deactivate dialog */}
      <Dialog open={deactOpen} onOpenChange={setDeactOpen}>
        <DialogContent data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Need a break?</DialogTitle>
            <DialogDescription>Deactivating your Nearfluence account will temporarily hide your profile and activity. Your information will be kept so you can return whenever you're ready.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            <Button data-testid="confirm-deactivate-btn" onClick={deactivate} disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Deactivate my account"}
            </Button>
            <Button variant="ghost" data-testid="keep-active-btn" onClick={() => setDeactOpen(false)} className="w-full rounded-full">Keep my account active</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete flow */}
      <Dialog open={step > 0} onOpenChange={(o) => !o && resetDelete()}>
        <DialogContent data-testid="delete-account-dialog">
          {step === 1 && (
            <>
              <DialogHeader>
                <DialogTitle>Are you sure you want to leave Nearfluence?</DialogTitle>
                <DialogDescription>Deleting your account is permanent. Your Nearfluence profile and associated account data will be deleted in accordance with our Privacy Policy. If you just need a break, you can deactivate your account instead.</DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-2">
                <Button data-testid="deactivate-instead-btn" onClick={() => { resetDelete(); setDeactOpen(true); }} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">Deactivate instead</Button>
                <Button variant="ghost" data-testid="continue-deletion-btn" onClick={() => setStep(2)} className="w-full rounded-full text-zinc-500">Continue to deletion</Button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <DialogHeader>
                <DialogTitle>What's making you leave Nearfluence?</DialogTitle>
                <DialogDescription>This is optional and helps us improve — you can skip it.</DialogDescription>
              </DialogHeader>
              <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
                {REASONS.map((r) => (
                  <label key={r} data-testid={`reason-${r}`} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-2.5 text-sm ${reason === r ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`}>
                    <input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} className="h-4 w-4 accent-emerald-600" />
                    <span className="text-zinc-700">{r}</span>
                  </label>
                ))}
                {reason === "Other" && (
                  <Input data-testid="reason-other-input" value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Tell us more (optional)" className="mt-1 h-10" />
                )}
              </div>
              <div className="mt-2 flex gap-2">
                <Button variant="ghost" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                <Button data-testid="reason-continue-btn" onClick={() => setStep(3)} className="flex-1 rounded-full bg-zinc-900 hover:bg-zinc-800">Continue</Button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <DialogHeader>
                <DialogTitle>Permanently delete your account?</DialogTitle>
                <DialogDescription>This action cannot be undone. Please confirm your password to continue. Some records may be retained in anonymised form where required by law or our Privacy Policy.</DialogDescription>
              </DialogHeader>
              <Input data-testid="delete-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="h-11" />
              <Button data-testid="delete-confirm-btn" disabled={!password || busy} onClick={deleteAccount} className="w-full rounded-full bg-rose-600 hover:bg-rose-700">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Permanently delete account"}
              </Button>
              <Button variant="ghost" data-testid="cancel-delete-btn" onClick={resetDelete} className="w-full rounded-full text-zinc-500">Cancel</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
